import track from './track';
import { useEffect, useState, useContext } from 'react';
import useCookie from 'edcom-ui/hooks/useCookie';
import { isRealBrowser } from './util';
import RequestContext from './RequestContext';

// Keep track of which tests were already bucketed so we only bucket once
const chosenVariations = new Map();

// Only fire tracking event once per testId per page view
const alreadyFired = new Map();
const fireTracking = (testId, variation) => {
  if (alreadyFired.has(testId)) return;
  track('Experiment Viewed', {
    experiment_id: testId,
    experiment_name: testId,
    variation_id: variation,
    variation_name: variation > 0 ? `Variation ${variation}` : 'Baseline',
  });
  alreadyFired.set(testId, true);
};

/**
 * @param {string} testId
 * @param {{buckets: number, coverage: number, weights: [number]}} options
 * @returns {[number|null, function(): void]} The variation and a callback for bucketing the user
 */
export default function useVariation(
  testId,
  { buckets = 2, coverage, weights } = {}
) {
  // Only allow between 2 and 4 buckets (can increase the top limit if we really need to)
  // This is meant to stop typos
  if (![2, 3, 4].includes(buckets)) {
    throw new Error(
      `AB test ${testId} can only have 2, 3, or 4 buckets. ${buckets} given.`
    );
  }

  // Ability to force a variation in the querystring
  const { query } = useContext(RequestContext);
  let queryOverride = null;
  if (testId in query) {
    if (
      query[testId].match(/^[0-9]$/) &&
      query[testId] >= 0 &&
      query[testId] < buckets
    ) {
      queryOverride = query[testId] * 1;
      chosenVariations.set(testId, queryOverride);
    } else {
      console.error(
        `Invalid test querystring. Must be an integer from 0 to ${buckets - 1}`,
        testId,
        query[testId]
      );
    }
  }

  // Keep track of variation and whether or not it's done restoring from querystring/cookie
  const [variation, setVariation] = useState(queryOverride);

  // Restore variation from cookie after page load if needed
  const [cookie, setCookie] = useCookie(`gace_${testId}`);
  const getRestoredVariation = () => {
    if (variation === null) {
      if (
        chosenVariations.has(testId) &&
        chosenVariations.get(testId) !== null
      ) {
        return chosenVariations.get(testId);
      }

      return cookie;
    }
    return null;
  };
  useEffect(() => {
    const restored = getRestoredVariation();
    if (restored !== null) {
      setVariation(restored);
    }
  }, []);

  // Track in Segment when a user is put into a variation
  useEffect(() => {
    // Only track if the user has been bucketed (not null or "-1")
    if (variation !== null && variation >= 0) {
      fireTracking(testId, variation);
    }
  }, [variation]);

  // Update the cookie whenever the variation changes to a non-null value
  useEffect(() => {
    if (variation !== null) {
      setCookie(variation, {
        // Keep for 30 days
        expires: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toUTCString(),
      });
    }
  }, [variation]);

  // Already bucketed
  if (variation !== null) {
    // Treat variation "-1" (not in test) the same as "null" (not bucketed)
    let returnedVariation = variation < 0 ? null : variation;

    // Convert non-null variations to integers to enable strict type checking in code
    if (returnedVariation !== null) {
      returnedVariation *= 1;
    }

    // Return a dummy bucketUser function
    return [returnedVariation, () => {}];
  }

  // Bot or SSR, return a dummy bucketUser callback
  if (!isRealBrowser()) {
    return [null, () => {}];
  }

  // Not bucketed yet, return a real bucketUser callback
  return [
    null,
    () => {
      // Catch edge case where this is called before the useEffect hook that restores already chosen variation
      const restored = getRestoredVariation();
      if (restored !== null) {
        setVariation(restored);
      }
      // Randomly place user in a bucket
      else {
        const chosen = bucketUser(testId, buckets, coverage, weights);
        setVariation(chosen);
        chosenVariations.set(testId, chosen);
      }
    },
  ];
}

/**
 * @param {string} testId
 * @param {number} buckets
 * @param {null|number} coverage
 * @param {null|number[]} weights
 * @returns {null|number}
 */
function bucketUser(testId, buckets, coverage, weights) {
  // Test does not run for 100% of users
  if (coverage) {
    if (coverage > 1 || coverage < 0) {
      console.error(
        'AB test coverage must be between 0 and 1',
        testId,
        coverage
      );
      return null;
    }

    const rand = Math.random();
    // If user should be excluded from the test
    if (rand > coverage) {
      // -1 means not in test
      return -1;
    }
  }

  // Test traffic is not split evenly between variations
  if (weights) {
    if (weights.length !== buckets) {
      console.error(
        'Number of AB test weights must equal number of variations',
        testId,
        weights,
        buckets
      );
      return null;
    }

    // Sum of all weights
    const totalWeight = weights.reduce((a, b) => a + b, 0);
    // Give some leeway for rounding errors
    if (totalWeight < 0.9 || totalWeight > 1.1) {
      console.error('AB test weights must add up to 1', testId, weights);
      return null;
    }

    // Walk through the weights and stop when we reach the random number
    const rand = Math.random();
    let cumulativeWeight = 0;
    for (let i = 0; i < weights.length; i++) {
      cumulativeWeight += weights[i];
      if (rand < cumulativeWeight / totalWeight) {
        return i;
      }
    }
  }
  // Test traffic IS split evenly between variations
  else {
    const r = Math.random();
    return Math.floor(r * buckets);
  }

  return null;
}
